import { useCallback, useEffect, useState, useSyncExternalStore } from "react";

export type A11yPrefs = {
  textSize: "A" | "A+" | "A++";
  highContrast: boolean;
  readableFont: boolean;
  underlineLinks: boolean;
  largerCursor: boolean;
  reduceMotion: boolean;
  pauseMedia: boolean;
};

export const DEFAULT_PREFS: A11yPrefs = {
  textSize: "A",
  highContrast: false,
  readableFont: false,
  underlineLinks: false,
  largerCursor: false,
  reduceMotion: false,
  pauseMedia: false,
};

export const A11Y_STORAGE_KEY = "a11y-prefs-v1";

// Shared module-level store: every consumer sees the same state, and the
// preferences are re-applied to <html> whenever the app mounts (client-side
// navigation keeps the module alive, so nothing is lost between pages).
let store: A11yPrefs = DEFAULT_PREFS;
let initialized = false;
const listeners = new Set<() => void>();

function readStorage(): A11yPrefs {
  if (typeof window === "undefined") return DEFAULT_PREFS;
  try {
    const raw = localStorage.getItem(A11Y_STORAGE_KEY);
    if (raw) return { ...DEFAULT_PREFS, ...JSON.parse(raw) } as A11yPrefs;
  } catch {
    /* noop */
  }
  return DEFAULT_PREFS;
}

export function applyPrefs(p: A11yPrefs) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  root.dataset.a11yTextSize = p.textSize;
  root.classList.toggle("a11y-high-contrast", p.highContrast);
  root.classList.toggle("a11y-readable-font", p.readableFont);
  root.classList.toggle("a11y-underline-links", p.underlineLinks);
  root.classList.toggle("a11y-larger-cursor", p.largerCursor);
  root.classList.toggle("a11y-reduce-motion", p.reduceMotion || p.pauseMedia);
  root.classList.toggle("a11y-pause-media", p.pauseMedia);
  if (p.pauseMedia) pauseAllMedia();
}

function pauseAllMedia() {
  document.querySelectorAll<HTMLMediaElement>("video, audio").forEach((el) => {
    el.autoplay = false;
    try {
      el.pause();
    } catch {
      /* noop */
    }
  });
}

// Keeps "Pause videos & animations" enforced for media that mounts later,
// e.g. a hero video on a page reached through client-side navigation.
let mediaGuardInstalled = false;
function installMediaGuard() {
  if (mediaGuardInstalled || typeof document === "undefined") return;
  mediaGuardInstalled = true;

  document.addEventListener(
    "play",
    (e) => {
      if (!store.pauseMedia) return;
      const el = e.target as HTMLMediaElement | null;
      if (!el || typeof el.pause !== "function") return;
      el.autoplay = false;
      try {
        el.pause();
      } catch {
        /* noop */
      }
    },
    true,
  );

  const mo = new MutationObserver((records) => {
    if (!store.pauseMedia) return;
    for (const rec of records) {
      for (const node of Array.from(rec.addedNodes)) {
        if (!(node instanceof Element)) continue;
        if (node.matches("video, audio") || node.querySelector("video, audio")) {
          pauseAllMedia();
          return;
        }
      }
    }
  });
  mo.observe(document.documentElement, { childList: true, subtree: true });
}

// React hydration can drop classes/attributes the boot script set on <html>,
// and route transitions re-render the tree, so re-assert the prefs on any
// change to the root element's class/attributes.
let rootGuardInstalled = false;
function installRootGuard() {
  if (rootGuardInstalled || typeof document === "undefined") return;
  rootGuardInstalled = true;
  let syncing = false;
  const mo = new MutationObserver(() => {
    if (syncing) return;
    syncing = true;
    applyPrefs(store);
    // Allow the observer to settle before listening again.
    setTimeout(() => {
      syncing = false;
    }, 0);
  });
  mo.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ["class", "data-a11y-text-size"],
  });
}

export function ensureA11yInit() {
  if (initialized || typeof window === "undefined") return;
  initialized = true;
  store = readStorage();
  applyPrefs(store);
  installMediaGuard();
  installRootGuard();
  window.addEventListener("storage", (e) => {
    if (e.key !== A11Y_STORAGE_KEY) return;
    store = readStorage();
    applyPrefs(store);
    listeners.forEach((l) => l());
  });
}

function setStore(next: A11yPrefs) {
  store = next;
  applyPrefs(store);
  try {
    localStorage.setItem(A11Y_STORAGE_KEY, JSON.stringify(store));
  } catch {
    /* noop */
  }
  listeners.forEach((l) => l());
}

function subscribe(l: () => void) {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
}

export function useA11yPrefs() {
  ensureA11yInit();
  const prefs = useSyncExternalStore(subscribe, () => store, () => DEFAULT_PREFS);
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => {
    setHydrated(true);
    // Re-assert after hydration in case React reconciled <html> attributes.
    applyPrefs(store);
  }, []);

  const update = useCallback((patch: Partial<A11yPrefs>) => {
    setStore({ ...store, ...patch });
  }, []);

  const reset = useCallback(() => {
    try {
      localStorage.removeItem(A11Y_STORAGE_KEY);
    } catch {
      /* noop */
    }
    setStore(DEFAULT_PREFS);
  }, []);

  return { prefs, update, reset, hydrated };
}
